import React, { useReducer, useEffect } from 'react'; // 👈 Import useEffect
import { useNavigate } from 'react-router-dom';
import UserContext from './userContext';
import UserReducer from './userReducer';
import { GET_USER, GET_USERS, SEARCH_USERS, SET_LOADING, SET_PAGINATION } from '../types';

// 1. 👈 Import the custom Axios instance and the setup function
// NOTE: Adjust the path to your axios service file if needed!
import axiosInstance, { setupGlobalInterceptor } from '../../services/axios.service';
import storage from '../../helpers/storage';
import { currentPath, loginUrlFor } from '../../helpers/redirect';


const UserState = (props) => {
    const navigate = useNavigate();

    const initialState = {
        users: [],
        user: {},
        search: [],
        pagination: {},
        loading: false
    };

    const [state, dispatch] = useReducer(UserReducer, initialState);

    // The centralized logout function passed to the interceptor
    const logout = () => {
        storage.clearAuth();
        // Back to where they were once they sign in again.
        navigate(loginUrlFor(currentPath()));
        setLoading(false); // 👈 Important: Set loading to false after redirect
    };

    // 2. 👈 Use useEffect to set up the interceptor once on mount
    useEffect(() => {
        setupGlobalInterceptor(logout);
    }, []); // Empty dependency array ensures it runs only once

    // NOTE: The `logout` function defined here is now only used as the callback 
    // for the interceptor and for any user-initiated logouts.

    const getUsers = async (limit, page) => {
        const q = `limit=${limit}&page=${page}`;
        setLoading();
        try {
            // 3. 👈 Switched from Axios to axiosInstance
            const resp = await axiosInstance.get(`${process.env.REACT_APP_BASE_URL}/admins?${q}`, storage.getConfigWithBearer());
            const paginate = {
                page: resp.data.data.page,
                hasNextPage: resp.data.data.hasNextPage,
                hasPreviousPage: resp.data.data.hasPreviousPage,
                nextPage: resp.data.data.nextPage,
                previousPage: resp.data.data.previousPage,
                limit: resp.data.data.limit,
                totalDocs: resp.data.data.totalDocs,
                totalPages: resp.data.data.totalPages
            };

            dispatch({
                type: SET_PAGINATION,
                payload: paginate
            });

            dispatch({
                type: GET_USERS,
                payload: resp.data.data.docs
            });

        } catch (error) {
            // 4. ❌ REMOVED manual 401 check, handled by global interceptor
            console.error('Error fetching users:', error);
        }
    };

    const getUser = async (userId) => {
        setLoading();
        try {
            // 3. 👈 Switched from Axios to axiosInstance
            const resp = await axiosInstance.get(`${process.env.REACT_APP_BASE_URL}/admins/${userId}`, storage.getConfigWithBearer());

            dispatch({
                type: GET_USER,
                payload: resp.data.data
            });
            setLoading(false);

        } catch (error) {
            // 4. ❌ REMOVED manual 401 check, handled by global interceptor
            console.error('Error fetching single user:', error);
        }
    };

    const setUser = (data) => {
        setLoading();
        dispatch({
            type: GET_USER,
            payload: data
        });
    };

    const searchUser = async (limit, page, keyword) => {
        const q = `limit=${limit}&page=${page}&keyword=${keyword}`;
        setLoading();
        try {
            // 3. 👈 Switched from Axios to axiosInstance
            const resp = await axiosInstance.get(`${process.env.REACT_APP_BASE_URL}/admins?${q}`, storage.getConfigWithBearer());

            const paginate = {
                page: resp.data.data.page,
                hasNextPage: resp.data.data.hasNextPage,
                hasPreviousPage: resp.data.data.hasPreviousPage,
                nextPage: resp.data.data.nextPage,
                previousPage: resp.data.data.previousPage,
                limit: resp.data.data.limit,
                totalDocs: resp.data.data.totalDocs,
                totalPages: resp.data.data.totalPages
            };

            dispatch({
                type: SET_PAGINATION,
                payload: paginate
            });

            dispatch({
                type: SEARCH_USERS,
                payload: resp.data.data.docs
            });
        } catch (error) {
            // 4. ❌ REMOVED manual 401 check, handled by global interceptor
            console.error('Error searching users:', error);
        }
    };

    const clearSearch = async () => {
        dispatch({
            type: SEARCH_USERS,
            payload: []
        });
    };

    // Updated setLoading to accept a boolean for clearer control flow
    const setLoading = (value = true) => { 
        dispatch({
            type: SET_LOADING,
            payload: value
        });
    };

    return (
        <UserContext.Provider value={{
            users: state.users,
            user: state.user,
            search: state.search,
            pagination: state.pagination,
            loading: state.loading,
            getUsers,
            getUser,
            searchUser,
            clearSearch,
            setUser,
            setLoading
        }}
        >
            {props.children}

        </UserContext.Provider>
    );
};

export default UserState;